import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import type { ActivityTrackKey } from "@/core/activity-levels/track-catalog"

import { toast } from "@/components/ui/toast"
import {
  activityLevelsApi,
  type AchievementDefinitionInput,
  type AdjustXpOperation,
  type TrackDefinitionInput,
} from "@/lib/activity-levels-api"
import { activityLevelsKeys } from "@/lib/query-keys"

const notifyMutationError = (error: Error) => toast.error(error.message)

export const useTracksQuery = () =>
  useQuery({
    queryKey: activityLevelsKeys.tracks(),
    queryFn: () => activityLevelsApi.listTracks(),
  })

export const useUpdateTrackMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: TrackDefinitionInput) =>
      activityLevelsApi.updateTrack(input.activityKey, input),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: activityLevelsKeys.tracks() }),
    onError: notifyMutationError,
  })
}

export const useRewardsQuery = (activityKey: ActivityTrackKey) =>
  useQuery({
    queryKey: activityLevelsKeys.rewards(activityKey),
    queryFn: () => activityLevelsApi.listRewards(activityKey),
  })

export const useAddRewardMutation = (activityKey: ActivityTrackKey) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { level: number; roleId: string }) =>
      activityLevelsApi.addReward(activityKey, input),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: activityLevelsKeys.rewards(activityKey),
      }),
    onError: notifyMutationError,
  })
}

export const useRemoveRewardMutation = (activityKey: ActivityTrackKey) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { level: number; roleId: string }) =>
      activityLevelsApi.removeReward(activityKey, input.level, input.roleId),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: activityLevelsKeys.rewards(activityKey),
      }),
    onError: notifyMutationError,
  })
}

export const useProgressQuery = (
  activityKey: ActivityTrackKey,
  userId: string | null,
) =>
  useQuery({
    queryKey: activityLevelsKeys.progress(activityKey, userId ?? ""),
    queryFn: () => activityLevelsApi.getProgress(activityKey, userId as string),
    enabled: userId != null,
  })

export const useAdjustXpMutation = (
  activityKey: ActivityTrackKey,
  userId: string,
) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { operation: AdjustXpOperation; amount?: number }) =>
      activityLevelsApi.adjustXp(activityKey, userId, input),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({
          queryKey: activityLevelsKeys.progress(activityKey, userId),
        }),
        qc.invalidateQueries({
          queryKey: activityLevelsKeys.leaderboard(activityKey),
        }),
      ]),
    onError: notifyMutationError,
  })
}

export const useLeaderboardQuery = (
  activityKey: ActivityTrackKey,
  limit?: number,
) =>
  useQuery({
    queryKey: activityLevelsKeys.leaderboard(activityKey, limit),
    queryFn: () => activityLevelsApi.getLeaderboard(activityKey, limit),
  })

export const useAchievementDefinitionsQuery = () =>
  useQuery({
    queryKey: activityLevelsKeys.achievementDefinitions(),
    queryFn: () => activityLevelsApi.listAchievementDefinitions(),
  })

export const useUpsertAchievementDefinitionMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      achievementKey: string
      definition: AchievementDefinitionInput
    }) =>
      activityLevelsApi.upsertAchievementDefinition(
        input.achievementKey,
        input.definition,
      ),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: activityLevelsKeys.achievementDefinitions(),
      }),
    onError: notifyMutationError,
  })
}

export const useDeleteAchievementDefinitionMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (achievementKey: string) =>
      activityLevelsApi.deleteAchievementDefinition(achievementKey),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: activityLevelsKeys.achievementDefinitions(),
      }),
    onError: notifyMutationError,
  })
}

export const useUserAchievementsQuery = (userId: string | null) =>
  useQuery({
    queryKey: activityLevelsKeys.userAchievements(userId ?? ""),
    queryFn: () => activityLevelsApi.listUserAchievements(userId as string),
    enabled: userId != null,
  })

export const useGrantAchievementMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { userId: string; achievementKey: string }) =>
      activityLevelsApi.grantAchievement(input.userId, input.achievementKey),
    onSuccess: (_data, variables) =>
      qc.invalidateQueries({
        queryKey: activityLevelsKeys.userAchievements(variables.userId),
      }),
    onError: notifyMutationError,
  })
}

export const useRevokeAchievementMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { userId: string; achievementKey: string }) =>
      activityLevelsApi.revokeAchievement(input.userId, input.achievementKey),
    onSuccess: (_data, variables) =>
      qc.invalidateQueries({
        queryKey: activityLevelsKeys.userAchievements(variables.userId),
      }),
    onError: notifyMutationError,
  })
}
